import { html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { componentText, SchmancyFormField, type ComponentTextKey } from '@schmancy/mixins/index'
import '../icons/icon'
import '@schmancy/form/fields/input/input'

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-date-range-inline': SchmancyDateRangeInline
	}
}

export type SchmancyDateRangeInlineChangeEvent = CustomEvent<{
	dateFrom: string
	dateTo: string
	isValid: boolean
}>

/**
 * A message this control has decided to show but has not put into words yet.
 * Validation picks WHICH message; `render()` picks the language, so a wording
 * swap moves an error already on screen. The sibling `<schmancy-date-range>`
 * instead re-runs `checkValidity()` on every update while in error — which this
 * control cannot do, because its own validation pass also auto-corrects the
 * endpoints and dispatches `change`.
 */
type PendingMessage = { key: ComponentTextKey; params?: Record<string, string | number> } | null

@customElement('schmancy-date-range-inline')
export default class SchmancyDateRangeInline extends SchmancyFormField() {
	@property({ type: String })
	type: 'date' | 'datetime-local' = 'date'

	#labelFrom: string | null = null
	#labelTo: string | null = null
	#valueFrom = ''
	#valueTo = ''

	/**
	 * The start endpoint. Its label left alone is the `dateRange.from` wording —
	 * the same key `<schmancy-date-range>`'s own panel reads, so one word moves
	 * both controls — resolved on every read rather than frozen at construction.
	 * Assigning takes ownership of the label the way any other prop does.
	 *
	 * Label and value are held in separate backing fields behind this accessor
	 * precisely so the component's own value writes never capture a label: this
	 * control sets both endpoints on its own initiative (smart defaults on
	 * connect, auto-correct on every edit), and a `{ ...this.dateFrom, value }`
	 * round-trip through the setter would pin whatever the table said at that
	 * moment — before a consumer has seen the field at all.
	 */
	@property({ type: Object })
	get dateFrom(): { label: string; value: string } {
		return { label: this.#labelFrom ?? componentText('dateRange.from'), value: this.#valueFrom }
	}
	set dateFrom(next: { label: string; value: string }) {
		this.#labelFrom = next.label
		this.#valueFrom = next.value
	}

	/** The end endpoint; `dateRange.to` is its default wording. Same shape as
	 *  `dateFrom` above, for the same reason. */
	@property({ type: Object })
	get dateTo(): { label: string; value: string } {
		return { label: this.#labelTo ?? componentText('dateRange.to'), value: this.#valueTo }
	}
	set dateTo(next: { label: string; value: string }) {
		this.#labelTo = next.label
		this.#valueTo = next.value
	}

	@property({ type: String })
	minDate?: string

	@property({ type: String })
	maxDate?: string

	@property({ type: Boolean })
	compact = false

	@property({ type: Boolean })
	autoCorrect = true

	@property({ type: Number })
	minGap = 0

	@property({ type: Number })
	maxGap?: number

	@property({ type: Number })
	defaultGap = 1

	@property({ type: Boolean })
	allowSameDate = false

	@state()
	private validationState: {
		dateFromError: PendingMessage
		dateToError: PendingMessage
		rangeError: PendingMessage
	} = {
		dateFromError: null,
		dateToError: null,
		rangeError: null,
	}

	connectedCallback() {
		super.connectedCallback()
		if (!this.dateFrom.value && !this.dateTo.value) {
			this.setSmartDefaults()
		} else {
			this.validateAndCorrect()
		}
	}

	/** Writes one endpoint's value, leaving its label alone — see `dateFrom`. */
	private setFrom(value: string) {
		this.#valueFrom = value
		this.requestUpdate()
	}

	private setTo(value: string) {
		this.#valueTo = value
		this.requestUpdate()
	}

	private setSmartDefaults() {
		const today = new Date()
		const tomorrow = new Date(today)
		tomorrow.setDate(tomorrow.getDate() + this.defaultGap)

		const format = this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'

		this.setFrom(this.formatDate(today, format))
		this.setTo(this.formatDate(tomorrow, format))
	}

	private formatDate(date: Date, format: string): string {
		const year = date.getFullYear()
		const month = String(date.getMonth() + 1).padStart(2, '0')
		const day = String(date.getDate()).padStart(2, '0')

		if (format === 'YYYY-MM-DD') {
			return `${year}-${month}-${day}`
		} else {
			const hours = String(date.getHours()).padStart(2, '0')
			const minutes = String(date.getMinutes()).padStart(2, '0')
			return `${year}-${month}-${day}T${hours}:${minutes}`
		}
	}

	private parseDate(dateStr: string): Date | null {
		if (!dateStr) return null
		const date = new Date(dateStr)
		return isNaN(date.getTime()) ? null : date
	}

	private getDaysBetween(date1: Date, date2: Date): number {
		const msPerDay = 24 * 60 * 60 * 1000
		return Math.floor((date2.getTime() - date1.getTime()) / msPerDay)
	}

	private handleDateFromChange(e: Event) {
		const input = e.target as HTMLInputElement
		const newValue = input.value

		this.setFrom(newValue)
		this.validationState = { ...this.validationState, dateFromError: null, rangeError: null }

		if (!newValue) {
			if (!this.required) {
				this.emitChange()
				return
			}
			this.validationState = {
				...this.validationState,
				dateFromError: { key: 'dateRangeInline.error.startMissing' },
			}
			this.error = true
			return
		}

		this.validateAndCorrect('from')
	}

	private handleDateToChange(e: Event) {
		const input = e.target as HTMLInputElement
		const newValue = input.value

		this.setTo(newValue)
		this.validationState = { ...this.validationState, dateToError: null, rangeError: null }

		if (!newValue) {
			if (!this.required) {
				this.emitChange()
				return
			}
			this.validationState = {
				...this.validationState,
				dateToError: { key: 'dateRangeInline.error.endMissing' },
			}
			this.error = true
			return
		}

		this.validateAndCorrect('to')
	}

	private validateAndCorrect(changedField?: 'from' | 'to') {
		const fromDate = this.parseDate(this.dateFrom.value)
		const toDate = this.parseDate(this.dateTo.value)

		let hasError = false
		const newValidationState: {
			dateFromError: PendingMessage
			dateToError: PendingMessage
			rangeError: PendingMessage
		} = { dateFromError: null, dateToError: null, rangeError: null }

		if (this.dateFrom.value && !fromDate) {
			newValidationState.dateFromError = { key: 'dateRangeInline.error.invalidFormat' }
			hasError = true
		}

		if (this.dateTo.value && !toDate) {
			newValidationState.dateToError = { key: 'dateRangeInline.error.invalidFormat' }
			hasError = true
		}

		if (fromDate && toDate) {
			const daysBetween = this.getDaysBetween(fromDate, toDate)

			if (fromDate > toDate) {
				if (this.autoCorrect) {
					if (changedField === 'from') {
						const newToDate = new Date(fromDate)
						newToDate.setDate(newToDate.getDate() + this.defaultGap)
						this.setTo(
							this.formatDate(newToDate, this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'),
						)
					} else if (changedField === 'to') {
						const newFromDate = new Date(toDate)
						newFromDate.setDate(newFromDate.getDate() - this.defaultGap)
						this.setFrom(
							this.formatDate(newFromDate, this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'),
						)
					} else {
						const tempValue = this.dateFrom.value
						this.setFrom(this.dateTo.value)
						this.setTo(tempValue)
					}
					this.validateAndCorrect()
					return
				} else {
					newValidationState.rangeError = { key: 'dateRangeInline.error.endBeforeStart' }
					hasError = true
				}
			}

			if (!this.allowSameDate && daysBetween === 0) {
				if (this.autoCorrect && changedField) {
					if (changedField === 'from') {
						const newToDate = new Date(fromDate)
						newToDate.setDate(newToDate.getDate() + this.defaultGap)
						this.setTo(
							this.formatDate(newToDate, this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'),
						)
					} else {
						const newFromDate = new Date(toDate)
						newFromDate.setDate(newFromDate.getDate() - this.defaultGap)
						this.setFrom(
							this.formatDate(newFromDate, this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'),
						)
					}
					this.validateAndCorrect()
					return
				} else {
					newValidationState.rangeError = { key: 'dateRangeInline.error.sameDate' }
					hasError = true
				}
			}

			if (this.minGap > 0 && daysBetween < this.minGap) {
				if (this.autoCorrect && changedField) {
					if (changedField === 'from') {
						const newToDate = new Date(fromDate)
						newToDate.setDate(newToDate.getDate() + this.minGap)
						this.setTo(
							this.formatDate(newToDate, this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'),
						)
					} else {
						const newFromDate = new Date(toDate)
						newFromDate.setDate(newFromDate.getDate() - this.minGap)
						this.setFrom(
							this.formatDate(newFromDate, this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'),
						)
					}
					this.validateAndCorrect()
					return
				} else {
					newValidationState.rangeError = {
						key: this.minGap > 1 ? 'dateRangeInline.error.minGap.other' : 'dateRangeInline.error.minGap.one',
						params: { count: this.minGap },
					}
					hasError = true
				}
			}

			if (this.maxGap !== undefined && daysBetween > this.maxGap) {
				if (this.autoCorrect && changedField) {
					if (changedField === 'from') {
						const newToDate = new Date(fromDate)
						newToDate.setDate(newToDate.getDate() + this.maxGap)
						this.setTo(
							this.formatDate(newToDate, this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'),
						)
					} else {
						const newFromDate = new Date(toDate)
						newFromDate.setDate(newFromDate.getDate() - this.maxGap)
						this.setFrom(
							this.formatDate(newFromDate, this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'),
						)
					}
					this.validateAndCorrect()
					return
				} else {
					newValidationState.rangeError = {
						key: this.maxGap > 1 ? 'dateRangeInline.error.maxGap.other' : 'dateRangeInline.error.maxGap.one',
						params: { count: this.maxGap },
					}
					hasError = true
				}
			}

			if (this.minDate) {
				const minDateObj = this.parseDate(this.minDate)
				if (minDateObj && fromDate < minDateObj) {
					newValidationState.dateFromError = { key: 'dateRangeInline.error.beforeMin' }
					hasError = true
				}
			}

			if (this.maxDate) {
				const maxDateObj = this.parseDate(this.maxDate)
				if (maxDateObj && toDate > maxDateObj) {
					newValidationState.dateToError = { key: 'dateRangeInline.error.afterMax' }
					hasError = true
				}
			}
		}

		this.validationState = newValidationState
		this.error = hasError

		this.emitChange()
	}

	private getComputedMinDateTo(): string | undefined {
		if (!this.dateFrom.value) return this.minDate

		const fromDate = this.parseDate(this.dateFrom.value)
		if (!fromDate) return this.minDate

		const minToDate = new Date(fromDate)

		if (!this.allowSameDate) {
			minToDate.setDate(minToDate.getDate() + 1)
		}

		if (this.minGap > 0) {
			minToDate.setDate(fromDate.getDate() + this.minGap)
		}

		if (this.minDate) {
			const globalMin = this.parseDate(this.minDate)
			if (globalMin && globalMin > minToDate) {
				return this.minDate
			}
		}

		return this.formatDate(minToDate, this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD')
	}

	private getComputedMaxDateFrom(): string | undefined {
		if (!this.dateTo.value) return this.maxDate

		const toDate = this.parseDate(this.dateTo.value)
		if (!toDate) return this.maxDate

		const maxFromDate = new Date(toDate)

		if (!this.allowSameDate) {
			maxFromDate.setDate(maxFromDate.getDate() - 1)
		}

		if (this.minGap > 0) {
			maxFromDate.setDate(toDate.getDate() - this.minGap)
		}

		if (this.maxDate) {
			const globalMax = this.parseDate(this.maxDate)
			if (globalMax && globalMax < maxFromDate) {
				return this.maxDate
			}
		}

		return this.formatDate(maxFromDate, this.type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD')
	}

	emitChange() {
		const isValid =
			!this.error &&
			!!this.dateFrom.value &&
			!!this.dateTo.value &&
			!this.validationState.dateFromError &&
			!this.validationState.dateToError &&
			!this.validationState.rangeError

		this.dispatchEvent(
			new CustomEvent<SchmancyDateRangeInlineChangeEvent['detail']>('change', {
				detail: {
					dateFrom: this.dateFrom.value,
					dateTo: this.dateTo.value,
					isValid,
				},
				bubbles: true,
				composed: true,
			}),
		)
	}

	protected render() {
		// The wording is read here rather than at validation time, so the table is
		// what a mounted, already-erroring field answers to.
		const fromError = this.validationState.dateFromError ?? this.validationState.rangeError
		const toError = this.validationState.dateToError ?? this.validationState.rangeError

		return html`
			<div class="w-full">
				<div class="flex items-start gap-2 w-full">
					<div class="flex-1">
						<schmancy-input
							.type=${this.type}
							.label=${this.dateFrom.label}
							.value=${this.dateFrom.value}
							.min=${this.minDate}
							.max=${this.getComputedMaxDateFrom()}
							@change=${this.handleDateFromChange}
							.error=${!!fromError}
							.hint=${fromError ? componentText(fromError.key, fromError.params) : ''}
							.required=${this.required}
							.disabled=${this.disabled}
							size=${this.compact ? 'sm' : 'md'}
						></schmancy-input>
					</div>

					<div class="flex items-center justify-center ${this.compact ? 'pt-8' : 'pt-10'} px-1">
						<schmancy-icon class="text-surface-onVariant opacity-50"> arrow_forward </schmancy-icon>
					</div>

					<div class="flex-1">
						<schmancy-input
							.type=${this.type}
							.label=${this.dateTo.label}
							.value=${this.dateTo.value}
							.min=${this.getComputedMinDateTo()}
							.max=${this.maxDate}
							@change=${this.handleDateToChange}
							.error=${!!toError}
							.hint=${toError ? componentText(toError.key, toError.params) : ''}
							.required=${this.required}
							.disabled=${this.disabled}
							size=${this.compact ? 'sm' : 'md'}
						></schmancy-input>
					</div>
				</div>
			</div>
		`
	}
}
